import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSession } from "../session/SessionContext";
import { fetchMyPreferences, saveMyPreferences, type PersonPreferences, type PreferencesResult } from "../api/preferences";
import { PreferencesContext, defaultPreferences } from "./preferencesContext";

/**
 * Holds the signed-in person's application preferences for the whole portal, so a preference changed on the
 * Preferences page applies at once on every other page. Loads them when someone signs in and drops back to
 * the defaults when they sign out. Must sit beneath the `SessionProvider`.
 *
 * @param props.children - The application.
 */
export function PreferencesProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { snapshot } = useSession();
  const signedIn = snapshot.status === "signed-in";
  const [preferences, setPreferences] = useState<PersonPreferences>(defaultPreferences);

  useEffect(() => {
    if (!signedIn) {
      setPreferences(defaultPreferences);
      return;
    }
    let cancelled = false;
    void fetchMyPreferences().then((result) => {
      // A failed read leaves the defaults in place: the portal then shows the warnings it always has. A preference the server does
      // not send (one older than the host it is talking to) keeps its default too.
      if (!cancelled && result.kind === "ok") {
        setPreferences({ ...defaultPreferences, ...result.preferences });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  const save = useCallback(
    async (next: PersonPreferences): Promise<PreferencesResult> => {
      const previous = preferences;
      setPreferences(next);
      const result = await saveMyPreferences(next);
      if (result.kind === "ok") {
        setPreferences({ ...defaultPreferences, ...result.preferences });
      } else {
        setPreferences(previous);
      }
      return result;
    },
    [preferences],
  );

  const value = useMemo(() => ({ preferences, save }), [preferences, save]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}
